import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import CoinRequests from "@/components/CoinRequests";
import { Send, ScanLine, Loader2, X } from "lucide-react";
import jsQR from "jsqr";

export default function PayPlayer({ onChange, frozen, transfersEnabled }) {
  const [form, setForm] = useState({ recipient: "", coins: "", note: "" });
  const [sugs, setSugs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [sending, setSending] = useState(false);
  const timer = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);

  const loadRecent = () => api.get("/bullion/recent-recipients").then(({ data }) => setRecent(data)).catch(() => {});
  useEffect(() => { loadRecent(); }, []);

  const onRecipientChange = (v) => {
    setForm({ ...form, recipient: v });
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setSugs([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/bullion/recipients?q=${encodeURIComponent(v.trim())}`); setSugs(data); }
      catch { setSugs([]); }
    }, 250);
  };

  const pick = (s) => { setForm({ ...form, recipient: s.code || s.email }); setSugs([]); };

  const send = async () => {
    if (!form.recipient.trim() || !Number(form.coins)) return toast.error("Enter a player and coin amount");
    setSending(true);
    try {
      const { data } = await api.post("/bullion/transfer", {
        recipient: form.recipient.trim(), coins: Number(form.coins), note: form.note || null });
      toast.success(`Paid ${Number(form.coins)} BLC to ${data.recipient_name} 🪙`);
      setForm({ recipient: "", coins: "", note: "" });
      loadRecent();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const stopScan = () => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const closeScan = () => { stopScan(); setScanOpen(false); setScanError(null); };

  const startScan = async () => {
    setScanError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      setScanning(true);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const tick = () => {
        if (!streamRef.current) return;
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(img.data, img.width, img.height);
          const text = (code?.data || "").trim().toUpperCase();
          if (text.startsWith("BLC-")) {
            closeScan();
            setForm((f) => ({ ...f, recipient: text }));
            toast.success(`Wallet ${text} scanned — enter the amount and send 🪙`);
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setScanError("Camera access was blocked — allow camera permission in your browser, or type the wallet code instead.");
    }
  };

  useEffect(() => () => stopScan(), []);

  return (
    <div className={`grid gap-8 ${transfersEnabled ? "lg:grid-cols-2" : ""}`} data-testid="pay-player">
      {transfersEnabled && (
        <div className="bg-pb-surface border border-pb-border" data-testid="send-coins-section">
          <div className="p-5 border-b border-pb-border">
            <div className="flex items-center gap-2 mb-1">
              <Send className="w-5 h-5 text-pb-red" />
              <span className="font-display font-bold text-lg uppercase">Send Coins to a Player</span>
            </div>
            <p className="text-xs text-zinc-500">Pay any member in BULLION by name, email or wallet code — or scan their wallet QR courtside. Free and instant.</p>
          </div>
          <div className="p-5 space-y-4">
            {recent.length > 0 && (
              <div data-testid="pay-player-recent">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Recent players</div>
                <div className="flex gap-3 flex-wrap">
                  {recent.map((r) => (
                    <button key={r.id} data-testid={`recent-player-${r.id}`} onClick={() => pick(r)}
                      className="flex flex-col items-center gap-1 group" title={r.name}>
                      <span className="rounded-full ring-2 ring-transparent group-hover:ring-pb-red transition-all">
                        <Avatar src={r.avatar} name={r.name} size={44} />
                      </span>
                      <span className="text-[10px] text-zinc-400 group-hover:text-pb-red max-w-[60px] truncate transition-colors">{(r.name || "").split(" ")[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="relative">
              <input data-testid="transfer-recipient" value={form.recipient} placeholder="Name, email or BLC-XXXXXX"
                onChange={(e) => onRecipientChange(e.target.value)} autoComplete="off"
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              {sugs.length > 0 && (
                <div data-testid="recipient-suggestions" className="absolute left-0 right-0 top-full mt-1 z-30 bg-pb-surface border border-pb-cyan/50 max-h-56 overflow-y-auto shadow-xl">
                  {sugs.map((s) => (
                    <button key={s.id} type="button" data-testid={`recipient-sug-${s.id}`} onClick={() => pick(s)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-pb-elevated transition-colors border-b border-pb-border/40 last:border-0">
                      <Avatar src={s.avatar} name={s.name} size={28} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{s.name}
                          {s.merchant && <span className="ml-2 text-[9px] uppercase tracking-widest text-pb-red border border-pb-red/50 px-1.5 py-0.5">Merchant</span>}
                        </span>
                        <span className="block text-[11px] text-zinc-500 font-mono truncate">{s.code || s.email}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="transfer-coins" type="number" min={0.01} value={form.coins} placeholder="Coins"
                onChange={(e) => setForm({ ...form, coins: e.target.value })}
                className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red font-mono" />
              <input data-testid="transfer-note" value={form.note} placeholder="Note (optional)"
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
            </div>
            <div className="flex gap-3">
              <button data-testid="transfer-send-btn" onClick={send} disabled={sending || frozen}
                className="flex-1 flex items-center justify-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
                {sending ? "Sending…" : <><Send className="w-3.5 h-3.5" /> Send Coins</>}
              </button>
              <button data-testid="scan-qr-toggle" onClick={() => { if (scanOpen) { closeScan(); } else { setScanOpen(true); } }}
                className={`flex items-center gap-2 border font-bold uppercase tracking-widest px-4 text-[10px] transition-colors ${scanOpen ? "border-pb-red text-pb-red" : "border-pb-border text-zinc-400 hover:border-pb-red hover:text-pb-red"}`}>
                {scanOpen ? <X className="w-3.5 h-3.5" /> : <ScanLine className="w-3.5 h-3.5" />} {scanOpen ? "Close" : "Scan QR"}
              </button>
            </div>
            {scanOpen && (
              <div className="max-w-xs mx-auto text-center" data-testid="pay-player-scan">
                <video ref={videoRef} playsInline muted
                  className={`w-full aspect-square object-cover bg-black border ${scanning ? "border-pb-red" : "border-pb-border"}`} />
                {!scanning ? (
                  <button data-testid="scan-start-btn" onClick={startScan}
                    className="mt-3 w-full flex items-center justify-center gap-2 bg-pb-elevated border border-pb-border text-xs font-bold uppercase tracking-widest py-2.5 hover:border-pb-red transition-colors">
                    <ScanLine className="w-4 h-4" /> Start Camera
                  </button>
                ) : (
                  <div className="mt-3 flex items-center justify-center gap-2 text-xs text-pb-red uppercase tracking-widest font-bold">
                    <Loader2 className="w-4 h-4 animate-spin" /> Point at a player's wallet QR…
                  </div>
                )}
                {scanError && <p data-testid="scan-error" className="mt-2 text-xs text-red-400">{scanError}</p>}
                <p className="mt-2 text-[11px] text-zinc-500">The other player's QR is at the top of their Wallet — scanning fills in their address automatically.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div data-testid="request-coins-section">
        <CoinRequests onChange={onChange} />
      </div>
    </div>
  );
}
